import classNames from 'classnames';
import produce from 'immer';
import { first, isEmpty } from 'lodash';
import { memo, useRef, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router';
import Footer from '../../app/Footer';
import PaSurveyHeader from '../../app/PaSurveyHeader';
import LoadingAnim from '../../components/LoadingAnim';
import { useData } from '../../report/use-data';
import { usePatientTreeData } from '../../report/use-patient-tree-data';
import { useDefaultStates } from '../hooks/use-default-states';
import { usePaSurveyParams, useSurveyState, AidState } from '../hooks/use-survey-state';
import { useRecoilValue } from 'recoil';
import { SystemLocaleKeys, useStaticLocales } from '../../admin/hooks/locales/use-static-locale';
import config from '../../config';
import { usePatientTransData } from '../../report/use-patient-trans-data';
import { useTranslations } from '../hooks/use-translations';
import { AthenaServices } from '../../lib/athena/athena-services';
import PatientOrgContainer from '../hooks/PatientOrgContainer';
import il1 from './assets/DoctorPatient.svg';
import il2 from './assets/HealthcareWorker.svg';
import { useMyVisitLazyQuery } from '@cyren/common-lib';
import { getPatientBasePath } from '../../app/route-utils';

function decrypt(encryptedText?: string | null) {
  if (encryptedText == null) return encryptedText;

  let decryptedText = '';
  for (let i = 0; i < encryptedText.length; i++) {
    const charCode = encryptedText.charCodeAt(i);
    const decryptedCharCode = charCode - 14;
    decryptedText += String.fromCharCode(decryptedCharCode);
  }
  return decryptedText;
}

async function getPatientDetails(patientId: string) {
  const result = await AthenaServices.getPatientDetails(patientId);
  return result;
}

function PaSurveyHomePage() {
  const currentAid = useRecoilValue(AidState);


  // aid가 있으면 리다이렉트를 비활성화
  const [{ dataState, isLoading }, { setDataState }] = useData({ 
    initData: true,
    disableRedirectOnInvalidReport: true 
  });
  const [{ reportState, patientDetails }, { setReportState, setSurveyState, startSurvey }] = useSurveyState();
  const [{ initDemo }] = useDefaultStates();
  const { slt, sltStr } = useStaticLocales();
  const [, { t }] = useTranslations();
  const { referralMessageId, orgId, shortId } = usePaSurveyParams();
  const location = useLocation();
  const patientBasePath = getPatientBasePath(location.pathname);
  const query = new URLSearchParams(location.search);
  const nav = useNavigate();
  const baseUrl = `${patientBasePath}/${orgId}/${shortId}`;

  const { org, survey, visit, errors } = dataState;
  
  // preload first tree
  const firstTreeKey = first(survey?.questionTreeKeys);

  usePatientTreeData({
    autoRefetch: true,
    treeKey: firstTreeKey,
  });

  const firstPageKeys = survey?.metadata?.frontPageKeys || [];

  const [{ loadingTrans }] = usePatientTransData({
    keys: firstPageKeys,
  });

  // Lazy Query for manual fetch
  const [fetchMyVisit, { loading: loadingVisit }] = useMyVisitLazyQuery();

  const isValid = reportState.acceptedTerms;
  // 이 로직이 최초 한 번만 실행되도록 useRef와 useEffect를 사용합니다.

  const patientId = patientDetails?.patientid;
  const encounterId = query.get('eid');
  const didFetchPatientRef = useRef(false);

  useEffect(() => {
    // 이미 실행했다면 더 이상 실행하지 않음
    if (didFetchPatientRef.current) return;
    didFetchPatientRef.current = true;
  // patientId, encounterId가 변할 일은 거의 없으나, query가 바뀌면 다시 실행될 수 있도록 함
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId, encounterId, setSurveyState, setReportState]);
  let customParamValue = query.get('custom1');
  if (customParamValue) {
    // a custom parameter was provided.  base64 decode it if we can
    // Basic regex to check if string contains only valid base64 characters
    const base64Pattern = /^[A-Za-z0-9+/=]+$/;
    // Check if input contains only base64 characters and length is a multiple of 4
    if (base64Pattern.test(customParamValue) && customParamValue.length % 4 === 0) {
      try {
        // Attempt to decode the string to see if it's a proper base64 encoded value
        customParamValue = atob(customParamValue);
      } catch (error) {
        // If an error occurs during decoding, it's not a valid base64 string.  Just go with 
        // what we found
      }
    }
  }

  let anonymizedId = query.get('aid');
  if (anonymizedId) {
    // a custom parameter was provided.  base64 decode it if we can
    // Basic regex to check if string contains only valid base64 characters
    const base64Pattern = /^[A-Za-z0-9+/=]+$/;
    // Check if input contains only base64 characters and length is a multiple of 4
    if (base64Pattern.test(anonymizedId) && anonymizedId.length % 4 === 0) {
      try {
        // Attempt to decode the string to see if it's a proper base64 encoded value
        anonymizedId = atob(anonymizedId);
      } catch (error) {
        // If an error occurs during decoding, it's not a valid base64 string.  Just go with 
        // what we found
      }
    }
  }

  if (errors && errors.length > 0) {
    // Check if it's the "Survey ID not found" error which might happen during initialization
    // and could be retried or might be a temporary state.
    // However, if it persists, we show the error.
    // For better UX, we might want to differentiate fatal errors from transient ones.
    
    // If the error is about missing ID but we're in the process of fixing it (e.g. visit creation)
    // we might want to show loading instead.
    
    return (
      <div className='flex flex-1 w-full items-center justify-center flex-col gap-4 p-5'>
        {/* <h3 className='text-xl font-bold text-error'>Error Loading Survey</h3> */}
        {/* <p className='text-center'>{errors[0]}</p> */}
        <LoadingAnim className='h-full p-10 w-full' />
        <p className='text-center text-gray-500'>Initializing survey...</p>
        <button className='btn btn-ghost btn-sm mt-4' onClick={() => window.location.reload()}>Refresh</button>
      </div>
    );
  }

  if (loadingTrans || org == null || survey == null || isLoading || loadingVisit) {
    return (
      <div className='flex flex-1 w-full'>
        <LoadingAnim className='h-full p-10 w-full' />
      </div>
    );
  }

  const titleKeyAcep = 'survey-title-acep';
  const titleKeyYc = 'survey-title-yc';
  const titleKeyCommon = 'survey-title';

  let titleKey: SystemLocaleKeys = survey.desc?.includes('#yc') ? titleKeyYc : titleKeyCommon;
  titleKey = survey.desc?.includes('#acep') ? titleKeyAcep : titleKeyCommon;

  const subtitleKeyAcep = 'survey-subtitle-acep';
  const subtitleKeyCommon = 'survey-subtitle';

  const subtitleKey: SystemLocaleKeys = survey.desc?.includes('#acep')
    ? subtitleKeyAcep
    : subtitleKeyCommon;

  const customTextReplacements = {
    custom1: customParamValue || "",
    anonymized_id: anonymizedId || ""
  };
  const titleLabel = t('front-page-title', customTextReplacements, { fallbackValue: null });
  const subtitleLabel = t('front-page-desc', customTextReplacements, { fallbackValue: null });

  return (
    <PatientOrgContainer.Provider>
      <div className='flex-col flex-1 w-full gap-5 px-5 pt-2'>
        <div className='flex-col flex-1 w-full gap-6'>
          <PaSurveyHeader />

        <div className='text-xl flex-col gap-3'>
          <div className='' />

          {referralMessageId ? (
            <img
              src={il2}
              className='w-[45%] max-w-[320px] sm:w-[38%] lg:w-[28%] lg:max-w-[280px]'
            />
          ) : (
            <div className="flex-center">
              <img
                src={il1}
                className='w-[70%] max-w-[520px] sm:w-[55%] lg:w-[45%] lg:max-w-[440px]'
              />
            </div>
          )}

          <div className='h-1' />

          <h4 className='font-bold sm'>{titleLabel || slt({ key: titleKey })}</h4>
          <p className='md text-light'>{subtitleLabel || slt({ key: subtitleKey })}</p>
        </div>

        <div className='form-control'>
          <label className='flex cursor-pointer label gap-3 items-start'>
            <input
              type='checkbox'
              checked={reportState.acceptedTerms}
              className='checkbox checkbox-primary checkbox-lg'
              onChange={(e) => {
                setReportState((st) =>
                  produce(st, (dr) => {
                    dr.acceptedTerms = e.target.checked;

                    // cheating a little bit here... storing the URL parameter if it was
                    // found into the system value map now, since when page loads the
                    // report state isn't ready to store values
                    if (!dr?.systemValueMap) dr.systemValueMap = {};
                    if (!isEmpty(customParamValue)) {
                      dr.systemValueMap = {
                        ...dr?.systemValueMap,
                        custom1: customParamValue,
                      };
                    }
                    if (!isEmpty(anonymizedId)) {
                      dr.systemValueMap = {
                        ...dr?.systemValueMap,
                        anonymized_id: anonymizedId,
                        lastName: anonymizedId,
                      };
                    }
                  }),
                );
              }}
            />
            <div className='flex-1 flex-center-y h-full'>
              <span className='label-text text-light text-base'>
                {slt({
                  key: 'agree-msg',
                  vars: [
                    <a
                      key='v1'
                      href={config.termsOfService}
                      target='_blank'
                      rel='noreferrer'
                      className='link whitespace-nowrap'
                    >
                      {slt({ key: 'terms-of-Use' })}
                    </a>,
                    <a
                      key='v2'
                      href={config.privacyPolicy}
                      target='_blank'
                      rel='noreferrer'
                      className='link whitespace-nowrap'
                    >
                      {slt({ key: 'privacy-policy' })}
                    </a>,
                  ],
                })}
              </span>
            </div>
          </label>
        </div>

        <div className='flex-col gap-2'>
          <button
            className={classNames('btn btn-lg btn-primary', !isValid && 'btn-disabled')}
            onClick={async () => {
              if (!isValid) return;
              
              // 이미 완료된 설문지인지 확인
              if (visit?.status === 'done' || visit?.archived) {
                nav(`${baseUrl}/terminated`);
                return;
              }
              
              // 설문 시작 전에 localStorage 정리
              try {
                // recoil-persist로 저장된 설문 상태들 정리 (AidState는 제외)
                const keysToRemove = [];
                for (let i = 0; i < localStorage.length; i++) {
                  const key = localStorage.key(i);
                  if (key && (
                    key.includes('AdminPatientSurveyState') || 
                    key.includes('PaReportState') ||
                    key.includes('PaSurveyState') ||
                    key.includes('PaErrorState')
                    // AidState는 제외 - aid 변경 시에도 유지되어야 함
                  )) {
                    keysToRemove.push(key);
                  }
                }
                keysToRemove.forEach(key => localStorage.removeItem(key));
                
              } catch (e) {
                console.warn('localStorage 정리 중 오류:', e);
              }
              
              // visit 데이터가 없으면 생성 후 시작 (createIfMissing: true)
              if (currentAid && !visit?.id && shortId) {
                console.log('Visit data missing, creating new visit...');
                try {
                  const { data } = await fetchMyVisit({
                    variables: {
                      input: {
                        shortId,
                        referralMessageId,
                        appointmentId: currentAid || undefined,
                        createIfMissing: true,
                      },
                    },
                  });
                  
                  // visit 데이터 업데이트
                  if (data?.myVisit?.patientVisit?.data) {
                    // Note: encryptVisit 처리가 필요할 수 있으나, useData에서 처리된 방식과 유사하게 처리해야 함.
                    // 여기서는 간단히 상태 업데이트만 시도하거나, 페이지 리로드를 유도할 수 있음.
                    // 하지만 useData에서 Recoil 상태를 업데이트하는 함수(setDataState)를 노출했다면 여기서 업데이트 가능.
                    // useData 훅을 수정하여 setDataState를 리턴하도록 해야 함.
                    // 현재 useData는 [state, { updateTree, setDataState, ... }] 형태임.
                    
                    // 데이터 갱신
                    // 여기서 수동으로 변환/암호화 로직을 다시 구현하기보다, 
                    // useData가 내부적으로 사용하는 로직을 활용하거나,
                    // 간단히 새로고침 하거나, startSurvey가 내부적으로 visit을 체크하므로 상태 업데이트가 필요함.
                    
                    // convVisit 등은 gql-tools/utils/convert에서 가져와야 함. 
                    // 여기서는 복잡성을 피하기 위해 일단 window.location.reload()를 하거나,
                    // useData 훅을 통해 visit을 업데이트해야 함.
                    
                    // 가장 깔끔한 방법: visit이 생성되었으므로, 다시 fetch를 하거나, 
                    // startSurvey() 호출 시점에 visit이 있어야 하는데, 
                    // setDataState를 통해 visit을 업데이트해주는 것이 좋음.
                    
                    // 임시로 reload 대신, startSurvey 로직이 visit.id를 참조하므로, 
                    // setDataState를 사용하여 visit을 업데이트해준다.
                    
                    // 하지만 암호화된 데이터를 복호화해야 하므로, useData 내부 로직을 타는 게 좋음.
                    // fetchMyVisit 결과는 이미 복호화되지 않은 상태일 수 있음 (서버에서 encrypt해서 줌).
                    // 클라이언트에서 decrypt해야 함.
                    
                    // 간단하게: visit이 없으면 create 후 페이지 리로드 (상태 초기화 및 데이터 다시 로드)
                    // window.location.reload();
                    // return;
                    
                    // 또는 더 우아하게:
                    // useData가 자동으로 데이터를 다시 로드하게 하거나...
                    // 여기서는 `createIfMissing: true`로 호출했으니 데이터가 왔을 것임.
                    
                    // startSurvey 함수 수정 없이, 여기서 Recoil State를 업데이트 하려면 convVisit 등이 필요함.
                    // 상단 import에 추가 필요할 수 있음.
                    
                    // 하지만 startSurvey 내부에서 visit?.id를 체크함.
                    // 따라서 여기서 visit을 생성하고 나서 startSurvey를 호출하면,
                    // startSurvey는 여전히 `visit` (useRecoilState로 가져온)을 참조하는데,
                    // 이 컴포넌트의 `visit` 변수는 렌더링 시점의 값임.
                    // 따라서 fetch 후 상태를 업데이트하지 않으면 startSurvey는 옛날 null visit을 봄.
                    
                    // 해결책:
                    // 1. 페이지 리로드 (가장 확실)
                    // 2. 상태 업데이트 (복잡)
                    
                    // 여기서는 1번 방법을 사용하되, 사용자 경험을 위해 로딩 표시 등을 고려.
                    // 하지만 요구사항은 "방문 기록을 생성한 후 설문을 시작하도록 수정"임.
                    // 리로드 없이 하려면 상태 업데이트가 필요함.
                    
                    window.location.reload();
                    return;
                  }
                } catch (err) {
                  console.error('Failed to create visit:', err);
                  // 에러 처리
                  return;
                }
              }

              if (currentAid) {
                // aid가 있을 때는 바로 설문 시작 (새로고침 없이)
                initDemo(false);
                startSurvey();
                return;
              }
              
              initDemo(false);
              startSurvey();
            }}
          >
            {sltStr({ key: 'btn-start', upper: true })}
          </button>
        </div>

        {/* <div className='h-5' /> */}
        </div>
        <Footer />
      </div>
    </PatientOrgContainer.Provider>
  );
}

export default memo(PaSurveyHomePage);
